import { useState, useEffect, useRef } from 'react'
import { THINKING_SAFETY_TIMEOUT_MS } from '../../core/contracts.js'
import { lastVisitorTurnHasCompletedReply } from '../../lib/thinking-latch.js'
import type { TranscriptItem } from 'types'

type SetTranscriptType = React.Dispatch<React.SetStateAction<TranscriptItem[]>>

/**
 * Visible assistant text — drop Thinking even while Live still marks the row streaming.
 * Visitor must not need Stop after a reply is on screen.
 * A completed reply still counts when merge/timestamp order leaves YOU last.
 */
export function shouldClearThinkingLatch(transcript: TranscriptItem[]): boolean {
  return lastVisitorTurnHasCompletedReply(transcript)
}

/** Composer lock: empty / user streams still block. Visible assistant text does not. */
export function hasComposerBlockingStream(transcript: TranscriptItem[]): boolean {
  return transcript.some(item => {
    if (item.status !== 'streaming') return false
    if (item.role === 'model' && (item.text?.trim() ?? '')) return false
    return true
  })
}

export function useThinkingState(
  setTranscript: SetTranscriptType,
  transcript: TranscriptItem[] = [],
) {
  const [isThinking, setIsThinking] = useState(false)
  const currentLoadingIdRef = useRef<string | null>(null)
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const resetThinkingTimeoutRef = useRef<(() => void) | null>(null)
  const toolsForMessageRef = useRef<Array<any>>([])
  const toolIndexByIdRef = useRef<Record<string, number>>({})

  useEffect(() => {
    if (isThinking && shouldClearThinkingLatch(transcript)) {
      setIsThinking(false)
    }
  }, [isThinking, transcript])

  useEffect(() => {
    if (!isThinking) {
      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current)
        thinkingTimeoutRef.current = null
      }
      resetThinkingTimeoutRef.current = null
      return
    }
    const fireReset = () => {
      const loadingIdToClear = currentLoadingIdRef.current
      currentLoadingIdRef.current = null
      thinkingTimeoutRef.current = null
      resetThinkingTimeoutRef.current = null
      setIsThinking(false)
      if (loadingIdToClear) {
        setTranscript(prev => {
          let changed = false
          const next = prev.flatMap(item => {
            if (item.id !== loadingIdToClear || item.isFinal) return [item]
            changed = true
            const emptyShell =
              item.role === 'model' && !(item.text?.trim()) && !item.attachment
            return emptyShell ? [] : [{ ...item, isFinal: true, status: 'complete' as const }]
          })
          return changed ? next : prev
        })
      }
    }
    thinkingTimeoutRef.current = setTimeout(fireReset, THINKING_SAFETY_TIMEOUT_MS)
    resetThinkingTimeoutRef.current = () => {
      if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current)
      thinkingTimeoutRef.current = setTimeout(fireReset, THINKING_SAFETY_TIMEOUT_MS)
    }
    return () => {
      if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current)
      thinkingTimeoutRef.current = null
      resetThinkingTimeoutRef.current = null
    }
  }, [isThinking, setTranscript])

  return {
    isThinking,
    setIsThinking,
    currentLoadingIdRef,
    thinkingTimeoutRef,
    resetThinkingTimeoutRef,
    toolsForMessageRef,
    toolIndexByIdRef
  }
}
